import {
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Card,
  Stack,
  Switch,
  FormControlLabel,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'

const StatusToggle = ({ checked, loading, onChange }) => (
  <FormControlLabel
    sx={{ ml: 0, mr: 0 }}
    control={
      <Switch
        size="small"
        color="success"
        checked={!!checked}
        disabled={loading}
        onChange={onChange}
        inputProps={{ 'aria-label': checked ? 'Disable slide' : 'Enable slide' }}
      />
    }
    label={
      loading ? (
        <CircularProgress size={14} />
      ) : (
        <Typography
          variant="caption"
          fontWeight={600}
          color={checked ? 'success.main' : 'text.secondary'}
        >
          {checked ? 'Enabled' : 'Disabled'}
        </Typography>
      )
    }
  />
)

const SlideItem = ({
  item,
  loadingSlideId,
  onToggle,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isToggling = loadingSlideId === item._id

  if (isMobile) {
    return (
      <Card variant="outlined" sx={{ p: 2, borderColor: '#e6e0f5', borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="caption" color="primary" fontWeight={600}>
                Structure
              </Typography>
              <Typography variant="body2">{item.foreground_structure || '—'}</Typography>
            </Box>
            {canEdit && (
              <StatusToggle
                checked={item.enabled}
                loading={isToggling}
                onChange={() => onToggle(item)}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Updated: {new Date(item.updatedAt).toLocaleString('en-GB')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onView(item)}
            >
              Preview
            </Button>
            {canEdit && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => onEdit(item)}
              >
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(item)}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>
    )
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: '#e6e0f5',
        borderRadius: 2,
        mb: 1.5,
        '&:last-child': { mb: 0 },
      }}
    >
      <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="primary" fontWeight={600}>
            Updated At
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {new Date(item.updatedAt).toLocaleString('en-GB')}
          </Typography>
          {canEdit && (
            <Box sx={{ mt: 1 }}>
              <StatusToggle
                checked={item.enabled}
                loading={isToggling}
                onChange={() => onToggle(item)}
              />
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="primary" fontWeight={600}>
            Structure
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {item.foreground_structure || '—'}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={2}>
          <Typography variant="caption" color="primary" fontWeight={600}>
            Preview
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Tooltip title="View Preview">
              <IconButton
                size="small"
                sx={{ bgcolor: '#ffc107', color: '#000', '&:hover': { bgcolor: '#e6ac00' } }}
                onClick={() => onView(item)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: 1,
          }}
        >
          {canDelete && (
            <Tooltip title="Delete slide">
              <IconButton color="error" size="small" onClick={() => onDelete(item)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Edit slide">
              <IconButton color="primary" size="small" onClick={() => onEdit(item)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    </Card>
  )
}

export default SlideItem
